import { createContext, use } from 'react';
import invariant from 'tiny-invariant';
import { useStore } from 'zustand';

import type { AttachmentStoreApi, AttachmentStoreState } from './attachment-store.js';

export const AttachmentStoreContext = createContext<AttachmentStoreApi | null>(null);

/**
 * Returns the enclosing pane's attachment store handle for imperative access
 * (e.g. `getState().consume()` inside transport callbacks). Throws if no
 * provider is mounted above the caller.
 *
 * @returns The scoped attachment store.
 */
export function useAttachmentStoreApi(): AttachmentStoreApi {
	const store = use(AttachmentStoreContext);
	invariant(store, 'AttachmentStoreContext not provided');
	return store;
}

/**
 * Subscribes to the enclosing pane's attachment store. Throws if no provider
 * is mounted above the caller.
 *
 * @param selector - Selects the slice of state to subscribe to.
 * @returns The selected slice, re-rendering the caller when it changes.
 */
export function useAttachmentStore<T>(selector: (state: AttachmentStoreState) => T): T {
	return useStore(useAttachmentStoreApi(), selector);
}
